import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import useWebSocket from "react-use-websocket";
import throttle from "lodash/throttle";
import { WEBSOCKET_URL } from "components/parts/OrderBook/constants";
import {
  appendTodayPriceFor1YTimeframe,
  dedupeChartData,
  lineData
} from "utils/coinPrices";
import { generateTimeFrameString } from "utils/prices";
import type { Translate } from "i18n/types";
import type { PriceOverview } from "types/prices";

const MAX_DATA_POINTS = 1000; // Limit to prevent memory bloat

type ChartDataPoint = { time: number; value: number };

const sortByTime = (a: ChartDataPoint, b: ChartDataPoint): number =>
  a.time - b.time;

interface TradeEvent {
  type: "trade";
  symbol: string;
  event_id: string;
  price: string;
  quantity: string;
  side: string;
  tid: number;
  timestamp: number;
}

interface ChartDataConfig {
  coinSymbol: string;
  timeframe: string;
  isTokenizedStock: boolean;
  priceData: {
    priceRange?: ChartDataPoint[];
  } | null;
  initialOverview: {
    priceOverview?: PriceOverview;
    priceRange?: ChartDataPoint[];
  };
  t: Translate<"prices">;
}

const convertTradeToDataPoint = (trade: TradeEvent): ChartDataPoint | null => {
  const price = parseFloat(trade.price);
  if (isNaN(price)) return null;

  // Validate timestamp format (should be milliseconds, convert to seconds)
  const timestamp =
    trade.timestamp >= 1e12 ? trade.timestamp / 1000 : trade.timestamp;
  return { time: Math.floor(timestamp), value: price };
};

const processTradeUpdates = (
  existingPoints: ChartDataPoint[],
  newTrades: TradeEvent[]
): ChartDataPoint[] => {
  if (newTrades.length === 0) {
    return existingPoints;
  }

  // Convert and validate trades
  const validTrades: ChartDataPoint[] = [];
  for (const trade of newTrades) {
    const dataPoint = convertTradeToDataPoint(trade);
    if (dataPoint) {
      validTrades.push(dataPoint);
    }
  }

  if (validTrades.length === 0) {
    return existingPoints;
  }

  // Sort new trades chronologically
  validTrades.sort(sortByTime);

  // Merge with existing points
  const canAppendDirectly =
    existingPoints.length === 0 ||
    validTrades[0].time >= existingPoints[existingPoints.length - 1].time;

  let mergedPoints: ChartDataPoint[];
  if (canAppendDirectly) {
    mergedPoints = existingPoints.concat(validTrades);
  } else {
    // Less common case: merge and sort
    mergedPoints = existingPoints.concat(validTrades);
    mergedPoints.sort(sortByTime);
  }

  // Apply memory limit
  return mergedPoints.length > MAX_DATA_POINTS
    ? mergedPoints.slice(-MAX_DATA_POINTS)
    : mergedPoints;
};

const useChartData = (config: ChartDataConfig) => {
  const [liveDataPoints, setLiveDataPoints] = useState<ChartDataPoint[]>([]);
  const tradeQueueRef = useRef<TradeEvent[]>([]);

  const isLiveMode = config.timeframe === "1h" && !config.isTokenizedStock;
  const liveSymbol = `${config.coinSymbol}USD`;
  const symbols = isLiveMode ? [liveSymbol] : [];
  const enabled = isLiveMode;

  // Throttled function to process queued trades and prevent race conditions
  const processQueuedTrades = useMemo(
    () =>
      throttle(() => {
        const queuedTrades = tradeQueueRef.current;
        if (queuedTrades.length === 0) return;

        tradeQueueRef.current = []; // Clear queue
        setLiveDataPoints((prev) => processTradeUpdates(prev, queuedTrades));
      }, 50),
    []
  );

  // Memoize the message handler to prevent websocket reconnections
  const handleMessage = useCallback(
    (e: MessageEvent) => {
      try {
        const data: TradeEvent = JSON.parse(e.data);

        // Handle direct trade events
        if (data?.type === "trade" && data?.symbol === liveSymbol) {
          // Queue single trade to prevent race conditions with throttled updates
          tradeQueueRef.current.push(data);
          processQueuedTrades();
        }
      } catch (error) {
        console.error("Failed to parse websocket message:", {
          error,
          symbol: liveSymbol,
          enabled,
          dataReceived: e.data,
          url: WEBSOCKET_URL
        });
      }
    },
    [enabled, liveSymbol, processQueuedTrades]
  );

  const { sendJsonMessage } = useWebSocket(
    WEBSOCKET_URL,
    {
      share: true,
      filter: () => false,
      onMessage: handleMessage,
      onOpen: () => {
        if (enabled && symbols.length > 0) {
          sendJsonMessage({
            type: "subscribe",
            subscriptions: [{ name: "l2_updates", symbols }]
          });
        }
      },
      onClose: () => {
        // Note: Cannot send unsubscribe message after connection is closed
        // The connection cleanup will happen automatically
      },
      onError: (error) => {
        console.error("WebSocket error:", {
          error,
          symbol: liveSymbol,
          enabled,
          url: WEBSOCKET_URL,
          symbols
        });
      }
    },
    enabled
  );

  // Clear live data when not in live mode
  useEffect(() => {
    if (!enabled) {
      setLiveDataPoints([]);
      tradeQueueRef.current = []; // Clear queue as well
    }
  }, [enabled]);

  // Cleanup throttle function on unmount
  useEffect(() => {
    return () => {
      processQueuedTrades.cancel();
    };
  }, [processQueuedTrades]);

  const displayTimeframe = useMemo(() => {
    return isLiveMode
      ? "LIVE"
      : generateTimeFrameString(config.timeframe, config.t);
  }, [isLiveMode, config.timeframe, config.t]);

  const priceData = useMemo(() => {
    const linedData = config.initialOverview?.priceRange
      ? lineData(config.initialOverview.priceRange)
      : [];

    if (
      (config.timeframe === "1y" || config.timeframe === "all") &&
      config.initialOverview?.priceOverview?.price
    ) {
      return appendTodayPriceFor1YTimeframe(
        config.priceData?.priceRange ?? linedData,
        Number(config.initialOverview.priceOverview.price)
      );
    }

    return config.priceData?.priceRange ?? linedData;
  }, [config.initialOverview, config.priceData, config.timeframe]);

  const chartData = useMemo(() => {
    const baseData = dedupeChartData(priceData ?? []);

    // For live mode, limit to last 60 data points (1 hour of minute data)
    const filteredData = isLiveMode ? baseData.slice(-60) : baseData;

    // Add all live data points if in LIVE mode
    if (isLiveMode && liveDataPoints.length > 0) {
      // Merge filtered data with live data points, sort by time, and dedupe
      const mergedData = [...filteredData, ...liveDataPoints];
      const sortedData = mergedData.sort(sortByTime);
      return dedupeChartData(sortedData);
    }

    return filteredData;
  }, [priceData, isLiveMode, liveDataPoints]);

  return {
    isLiveMode,
    displayTimeframe,
    chartData
  };
};

export default useChartData;
